import React from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { authService } from "@/services/authService";
import useToastStore from "@/components/Common/Toast/toastStore";

const ResetPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
    password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], "Passwords must match")
    .required("Confirm Password is required"),
});

const ResetPassword = ({token}) => {
  const { token: urlToken } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');
  const { showToast } = useToastStore;

  console.log("Retrieved email from URL:", email); // Check the value of email
  const navigate = useNavigate();

  const handleSubmit = async (values, { setSubmitting }) => {
    
    try {
      await authService.ResetPassword({
        ...values,  
        token:urlToken
      });
      showToast({ message: "Password Reset successfully!", type: "success" });
      // navigate("/");
      navigate("/login");
    } catch (error) {
      // Display validation errors from Yup
      if (error.name === 'ValidationError') {
        showToast({ message: error.message, type: "error" });
        return;
      }

      // Handle backend errors
      if (error.response?.data) {
        const { errors, error: errorObj, message } = error.response.data;

        if (errors || errorObj) {
          // Handle structured error objects
          const errorMessages = Object.values(errors || errorObj).flat();
          errorMessages.forEach(msg => showToast({ message: msg, type: "error" }));
        } else if (message) {
          // Handle simple message error
          showToast({ message: message, type: "error" });
        }
      } else {
        // Handle generic errors
        showToast({ message: error.message || "Fail to reset password", type: "error" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background to-primary/10 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl w-full space-y-8 bg-card p-10 rounded-xl shadow-lg">
        <div className="-mx-4 flex flex-wrap items-center">
          <div className="w-full px-4 lg:w-1/2">
            <div className="text-center grayscale">
              <img
                src="https://cdn.tailgrids.com/2.0/image/application/images/forms/forms-10.svg"
                alt="illustration"
                className="mx-auto"
              />
            </div>
          </div>
          {/* Right side with illustration */}
          <div className="w-full px-4 lg:w-1/2">
            <div>
              <h2 className="mt-6 text-3xl font-extrabold text-foreground">App Trkr</h2>
              <p className="mt-2 text-sm text-foreground mb-8">
                Please enter your new password and confirm password to reset your account.
              </p>
            </div>

            <Formik
              initialValues={{
                email: email || "",
                password: "",
                password_confirmation: "",
                token: urlToken || "",
              }}
              validationSchema={ResetPasswordSchema}
              onSubmit={handleSubmit}
            >
              {({ errors, touched, isSubmitting, getFieldProps, values, setFieldValue }) => (
                <Form className="space-y-6">
                  <div className="rounded-md">
                    <div className="mb-4">
                      <label htmlFor="email" className="block text-sm font-medium text-foreground">
                        Email Address
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={email || ""}
                        readOnly
                        disabled
                        className="mt-1 appearance-none relative block w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground/50 rounded-md focus:outline-none focus:ring-1 focus:ring-ring focus:border-input focus:z-10 sm:text-sm"
                        placeholder="Enter your email"
                      />
                      {touched.email && errors.email && (
                        <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label htmlFor="password" className="block text-sm font-medium text-foreground">
                        Password
                      </label>
                      <input
                        id="password"
                        type="password"
                        {...getFieldProps("password")}
                        className="mt-1 appearance-none relative block w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground/50 rounded-md focus:outline-none focus:ring-1 focus:ring-ring focus:border-input focus:z-10 sm:text-sm"
                        placeholder="Enter your password"
                      />
                      {touched.password && errors.password && (
                        <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label htmlFor="password_confirmation" className="block text-sm font-medium text-foreground">
                        Confirm Password
                      </label>
                      <input
                        id="password_confirmation"
                        type="password"
                        {...getFieldProps("password_confirmation")}
                        className="mt-1 appearance-none relative block w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground/50 rounded-md focus:outline-none focus:ring-1 focus:ring-ring focus:border-input focus:z-10 sm:text-sm"
                        placeholder="Enter your confirm password"
                      />
                      {touched.password_confirmation && errors.password_confirmation && (
                        <p className="mt-2 text-sm text-red-600">{errors.password_confirmation}</p>
                      )}
                    </div>

                    <input
                      id="token"
                      type="hidden"
                      {...getFieldProps("token")}
                    />
                  </div>

                  

                  <div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-150 hover:scale-[1.02]"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : (
                        "Submit"
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
